import { useState, useEffect } from 'preact/hooks';
import { api } from './api';
import { getEffectiveRealtimeEndDate } from './dateRange';
import { appendDashboardFiltersToSearchParams, dashboardFilters, dateRangePreset, selectedProperty, startDate, endDate, isDashboardFilterActive, toggleDashboardFilter } from './state';
import type { DashboardFilterField } from './state';

type DevicesProps = {
    liveRefreshTick?: number;
};

const Devices = ({ liveRefreshTick = 0 }: DevicesProps) => {
    const [devicesData, setDevicesData] = useState<{ device_name: string; visits: number }[]>([]);
    const [loading, setLoading] = useState(true);
    const [groupBy, setGroupBy] = useState('browser');
    const [showDialog, setShowDialog] = useState(false);
    const [dialogData, setDialogData] = useState<{ device_name: string; visits: number }[]>([]);
    const [totalVisits, setTotalVisits] = useState(0);

    const buildDevicesUrl = (limit?: number) => {
        const params = new URLSearchParams({
            property: selectedProperty.value,
            start: startDate.value,
            end: getEffectiveRealtimeEndDate(endDate.value),
            groupBy,
        });
        appendDashboardFiltersToSearchParams(params);

        if (typeof limit === 'number') {
            params.set('limit', String(limit));
        }

        return `/devices?${params.toString()}`;
    };

    const loadDevices = async (options?: { limit?: number; showLoading?: boolean; updateDialog?: boolean }) => {
        if (!selectedProperty.value) {
            return;
        }

        const { limit, showLoading = true, updateDialog = false } = options ?? {};

        if (showLoading) {
            setLoading(true);
        }

        const result = await api(buildDevicesUrl(limit));

        if (updateDialog) {
            setDialogData(result.data);
            return;
        }

        setDevicesData(result.data);
        setTotalVisits(result.totalVisits);
        setLoading(false);
    };

    useEffect(() => {
        if (selectedProperty.value) {
            loadDevices({ showLoading: true }).catch(error => {
                console.error('Error fetching devices data:', error);
                setLoading(false);
            });
        }
    }, [selectedProperty.value, startDate.value, endDate.value, groupBy, JSON.stringify(dashboardFilters.value)]);

    useEffect(() => {
        if (!selectedProperty.value) {
            return;
        }

        if (dateRangePreset.value !== 'live' || liveRefreshTick === 0) {
            return;
        }

        loadDevices({ showLoading: false }).catch(error => {
            console.error('Error refreshing devices data:', error);
        });
    }, [liveRefreshTick, groupBy, JSON.stringify(dashboardFilters.value)]);

    const openDialog = () => {
        setShowDialog(true);
        loadDevices({ limit: 1000, showLoading: false, updateDialog: true }).catch(error => {
            console.error('Error fetching full devices data:', error);
        });
    };

    const getTitle = () => {
        switch (groupBy) {
            case 'operating_system': return 'Sistema Operativo';
            case 'screen_size': return 'Risoluzione';
            default: return 'Browser';
        }
    };

    const getFilterField = (): DashboardFilterField => {
        switch (groupBy) {
            case 'operating_system':
                return 'operating_system';
            case 'screen_size':
                return 'screen_size';
            default:
                return 'browser';
        }
    };

    const renderTable = (data: typeof devicesData, showProgress = false) => (
        <table className="data-table">
            <thead>
                <tr>
                    <th>{getTitle()}</th>
                    <th className="table-cell-number">Visite</th>
                    <th className="table-cell-percentage">%</th>
                </tr>
            </thead>
            <tbody>
                {data.map((device) => {
                    const percentage = totalVisits > 0 ? ((device.visits / totalVisits) * 100) : 0;
                    const percentageStr = percentage.toFixed(1);
                    const style = showProgress ? { '--progress-width': `${percentage}%` } as any : undefined;
                    const filterField = getFilterField();
                    return (
                        <tr
                            key={device.device_name}
                            style={style}
                            className={`filterable-row ${isDashboardFilterActive(filterField, device.device_name) ? 'is-filter-active' : ''}`}
                            onClick={() => toggleDashboardFilter(filterField, device.device_name)}
                        >
                            <td className="table-cell-primary">
                                {device.device_name || '(Sconosciuto)'}
                            </td>
                            <td className="table-cell-number">{device.visits}</td>
                            <td className="table-cell-percentage">{percentageStr}%</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    return (
        <>
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">{getTitle()}</h3>
                    <select 
                        className="select-input card-select"
                        value={groupBy} 
                        onChange={(e) => setGroupBy((e.target as HTMLSelectElement).value)}
                    >
                        <option value="browser">Browser</option>
                        <option value="operating_system">Sistema Operativo</option>
                        <option value="screen_size">Risoluzione</option>
                    </select>
                </div>
                {loading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                    </div>
                ) : devicesData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">💻</div>
                        <p className="empty-state-text">Nessun dato disponibile</p>
                    </div>
                ) : (
                    <>
                        {renderTable(devicesData, true)}
                        <button className="details-button" onClick={openDialog}>
                            Visualizza tutti i dettagli
                        </button>
                    </>
                )}
            </div>

            {showDialog && (
                <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
                    <div className="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h2 className="dialog-title">{getTitle()} - Dettagli completi</h2>
                            <button className="dialog-close" onClick={() => setShowDialog(false)}>×</button>
                        </div>
                        <div className="dialog-body">
                            {renderTable(dialogData, true)}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Devices;
